import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { money, shortDate } from '@/lib/format'
import { presentation } from '@/lib/quotationStatus'
import { useAsync } from '@/lib/useAsync'
import { FilterSelect, ListToolbar, Pagination, DEFAULT_PAGE_SIZE } from './ui/listing'
import { Badge, Busy, Card, CardHeader, EmptyState, ErrorState } from './ui/primitives'
import { Table, Td, Th, Tr } from './ui/table'
import type { Paged, QuotationListItem, QuotationStatus } from '@/types/api'

/**
 * Everything quoted for one party.
 *
 * Reads through the ordinary quotations list with a filter rather than a bespoke route, so
 * the record scope that applies everywhere else applies here for free: two people opening
 * the same customer can see a different number of quotations, and that is the access rule
 * working rather than a bug.
 */
export function PartyQuotations({
  filter,
  hint,
}: {
  /** `{ customerId }` or `{ resellerId }` - whichever party owns this page. */
  filter: Record<string, string>
  hint: string
}) {
  const navigate = useNavigate()
  const [status, setStatus] = useState<QuotationStatus | ''>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  function refine(apply: () => void) {
    apply()
    setPage(1)
  }

  const key = JSON.stringify(filter)

  const quotations = useAsync<Paged<QuotationListItem>>(
    (signal) => api.get<Paged<QuotationListItem>>('/quotations', { ...filter, status, page, pageSize }, signal),
    [key, status, page, pageSize],
  )

  return (
    <Card>
      <CardHeader title="Quotations" hint={hint} />

      <div className="px-5 pt-4">
        <ListToolbar
          sort=""
          onSortChange={() => undefined}
          sortChoices={[]}
        >
          <FilterSelect label="Status" value={status} onChange={(next) => refine(() => setStatus(next as QuotationStatus | ''))}>
            <option value="">All statuses</option>
            <option value="Draft">Draft</option>
            <option value="PendingApproval">Pending approval</option>
            <option value="Approved">Approved</option>
            <option value="Sent">Sent</option>
            <option value="Won">Won</option>
            <option value="Lost">Lost</option>
            <option value="Rejected">Rejected</option>
            <option value="Expired">Expired</option>
            <option value="Cancelled">Cancelled</option>
          </FilterSelect>
        </ListToolbar>
      </div>


      {quotations.error && (
        <div className="px-5 py-4">
          <ErrorState
            title={quotations.error.title}
            detail={quotations.error.detail}
            onRetry={quotations.reload}
          />
        </div>
      )}

      {quotations.data && quotations.data.items.length === 0 && (
        <EmptyState
          title={status ? 'Nothing with that status' : 'Nothing quoted yet'}
          hint={status ? 'Try another status.' : 'Quotations raised for this record appear here.'}
        />
      )}

      {quotations.data && quotations.data.items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>Number</Th>
              <Th>Customer</Th>
              <Th>Status</Th>
              <Th>Issued</Th>
              <Th numeric>Value</Th>
            </tr>
          </thead>
          <tbody>
            {quotations.data.items.map((quotation) => (
              <Tr key={quotation.id} onClick={() => navigate(`/quotations/${quotation.id}`)}>
                <Td>
                  {/* A draft has no number until it is submitted, so say so rather than
                      printing an empty cell that reads as missing data. */}
                  <span className="font-mono text-[12.5px]">{quotation.quotationNo ?? 'Draft'}</span>
                  {quotation.revisionNo > 1 && (
                    <span className="ml-2 text-[12px] text-faint">rev {quotation.revisionNo}</span>
                  )}
                </Td>
                <Td className="text-muted">{quotation.customerName}</Td>
                <Td>
                  <Badge tone={presentation(quotation.status).tone}>
                    {presentation(quotation.status).label}
                  </Badge>
                </Td>
                <Td className="text-muted">{shortDate(quotation.issueDate)}</Td>
                <Td numeric className="tabular">
                  {money(quotation.grandTotal, quotation.currencySymbol, 2)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Busy busy={quotations.loading}>
        {quotations.data && (
          <Pagination
            page={quotations.data.page}
            pageSize={quotations.data.pageSize}
            totalCount={quotations.data.totalCount}
            totalPages={quotations.data.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => refine(() => setPageSize(next))}
          />
        )}
      </Busy>
    </Card>
  )
}
